const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

function getToken(): string {
  if (typeof window !== "undefined") {
    return localStorage.getItem("hitmark_token") || "";
  }
  return "";
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function login(
  username: string,
  password: string
): Promise<{ token: string; username: string }> {
  const res = await fetch(`${API_URL}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Blad logowania");
  }
  const data = await res.json();
  localStorage.setItem("hitmark_token", data.token);
  localStorage.setItem("hitmark_user", data.username);
  return data;
}

export function logout() {
  localStorage.removeItem("hitmark_token");
  localStorage.removeItem("hitmark_user");
}

export function getStoredUser(): string | null {
  if (typeof window !== "undefined") {
    return localStorage.getItem("hitmark_user");
  }
  return null;
}

export interface Portal {
  name: string;
  address: string;
}

export interface Contract {
  registration_number: string;
  contract_number: string;
  contract_date: string | null;
  contract_value: number | null;
  is_active: boolean;
}

/** Unique key for a contract row (handles multi-vehicle contracts). */
export function contractKey(c: Contract): string {
  return c.registration_number
    ? `${c.contract_number}::${c.registration_number}`
    : c.contract_number;
}

export interface InvoiceData {
  capital: number;
  interest: number;
  additions: number;
  net_value: number;
  vat_value: number;
  gross_value: number;
}

export interface CalculationResult {
  registration_number: string;
  contract_number: string;
  contract_date: string | null;
  invoice_number: string;
  contract_value: number;
  car_value: number;
  percentage_coefficient: number;
  invoice: InvoiceData;
  konto_249: number;
  konto_751: number;
  additions: number;
  kup_tax: number | null;
  kup_capital_coefficient: number | null;
  kup_50_vat_capital: number | null;
  kup_interest: number | null;
  kup_50_vat_interest: number | null;
  exceeds_limit: boolean;
  is_vehicle: boolean;
}

export async function getPortals(): Promise<Portal[]> {
  const res = await fetch(`${API_URL}/api/portals`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch portals");
  return res.json();
}

export async function connectPortal(
  portalName: string
): Promise<{ status: string; portal: string; contracts: Contract[] }> {
  const res = await fetch(`${API_URL}/api/portals/${encodeURIComponent(portalName)}/connect`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to connect to portal");
  return res.json();
}

export async function expandContract(
  portalName: string,
  contractNumber: string
): Promise<{ contracts: Contract[] }> {
  const res = await fetch(
    `${API_URL}/api/portals/${encodeURIComponent(portalName)}/expand/${encodeURIComponent(contractNumber)}`,
    { method: "POST", headers: authHeaders() }
  );
  if (!res.ok) throw new Error("Failed to expand contract");
  return res.json();
}

export async function calculateContracts(
  portalName: string,
  selectedKeys: string[],
  year: number,
  month: number
): Promise<{ results: CalculationResult[] }> {
  const res = await fetch(`${API_URL}/api/portals/${encodeURIComponent(portalName)}/calculate?year=${year}&month=${month}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(selectedKeys),
  });
  if (!res.ok) throw new Error("Failed to calculate");
  return res.json();
}

/** Calculate one contract group at a time, calling onProgress for each. */
export async function calculateContractsWithProgress(
  portalName: string,
  selectedKeys: string[],
  year: number,
  month: number,
  onProgress: (regNumber: string) => void
): Promise<CalculationResult[]> {
  // Group keys by contract number
  const groups = new Map<string, string[]>();
  for (const key of selectedKeys) {
    const cn = key.split("::")[0];
    if (!groups.has(cn)) groups.set(cn, []);
    groups.get(cn)!.push(key);
  }

  const allResults: CalculationResult[] = [];

  for (const [, keys] of groups) {
    // Show first reg number of this group
    const firstReg = keys[0].includes("::") ? keys[0].split("::")[1] : keys[0];
    onProgress(firstReg);

    const res = await fetch(
      `${API_URL}/api/portals/${encodeURIComponent(portalName)}/calculate?year=${year}&month=${month}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(keys),
      }
    );
    if (!res.ok) throw new Error("Failed to calculate");
    const data = await res.json();
    allResults.push(...data.results);
  }

  return allResults;
}

export async function demoCalculate(
  contracts: Array<{
    registration_number: string;
    contract_number: string;
    contract_date?: string;
    contract_value: number;
    invoice: InvoiceData;
  }>
): Promise<{ results: CalculationResult[] }> {
  const res = await fetch(`${API_URL}/api/demo/calculate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(contracts),
  });
  if (!res.ok) throw new Error("Failed to calculate");
  return res.json();
}

export async function demoParsePdf(): Promise<InvoiceData> {
  const res = await fetch(`${API_URL}/api/demo/parse-pdf`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to parse PDF");
  return res.json();
}

export function formatPLN(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 2,
  }).format(value);
}
